import { Activity, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import UserAvatar from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import EmptyState from "@/components/common/EmptyState";
import { formatRelativeTime } from "@/utils/common.utils";
import type { ActivityItem } from "@/types/overview.types";

interface RecentActivityProps {
  /** Real cross-deal events from `db.activities`. This used to be deals sorted
   *  by update time, which meant one row per deal no matter how much had
   *  happened on it, and no row at all for anything that didn't touch the deal
   *  document. */
  items: ActivityItem[];
}

export default function RecentActivity({ items }: RecentActivityProps) {
  return (
    <Card className="flex h-[460px] flex-col overflow-hidden rounded-2xl border-dash-border shadow-none" data-testid="dashboard-recent-activity">
      <CardHeader className="flex-row items-center justify-between px-5 py-4">
        <CardTitle className="text-sm text-dash-ink">Recent activity</CardTitle>
        <Button variant="ghost" size="sm" className="hover:bg-dash-searchBg hover:text-dash-ink" asChild>
          <Link to="/dashboard"><span>View all</span><ArrowRight data-icon="inline-end" /></Link>
        </Button>
      </CardHeader>
      <Separator className="bg-dash-border" />
      <CardContent className="min-h-0 flex-1 overflow-y-auto p-0">
        {items.length === 0 ? (
          <EmptyState compact icon={Activity} title="No recent activity" description="Deal updates from your team will appear here as work progresses." className="h-full justify-center rounded-none border-0" />
        ) : items.map((item, index) => (
          <div key={item.id}>
            {index > 0 && <Separator className="bg-dash-borderSoft" />}
            <Link
              to={item.deal_id ? `/deals/${item.deal_id}` : "/dashboard"}
              className="flex min-h-[66px] items-center gap-3 px-5 py-3 transition-colors hover:bg-dash-searchBg"
            >
              <UserAvatar name={item.user_name} src={item.photo_url} className="size-8" />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-xs leading-4 text-dash-ink">
                  <strong>{item.user_name || "Your team"}</strong> {item.message}
                  {item.deal_address && <> — <strong>{item.deal_address}</strong></>}
                </p>
                <p className="mt-1 text-[10px] text-dash-faint">{formatRelativeTime(item.created_at)}</p>
              </div>
            </Link>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
